import { Location } from 'history';
import React from 'react';
import { connect } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';

import LoggedInUser from '@/models/interfaces/LoggedInUser';

import FetchContainer, { ConnectFetchParameter } from '../components/dataFetching/FetchContainer';
import { ACTIVATE_ACCOUNT_URL, DEACTIVATED_ACCOUNT, LOGIN_URL, PLACES, protectedRoutes, REGISTRATION_URL, ROOT_URL } from '../constants/RouterConstants';
import { fetchLoggedInUser } from '../store/actions/authentication/fetchLoggedInUser';
import { Store } from '../store/reducers/reducers';
import authenticationTokenSelector, { loggedInUserSelector } from '../store/selectors/authenticationSelectors';
import { loggedInUserFetchStateSelector } from '../store/selectors/fetchStateSelectors';

interface OwnProps {
  children: React.ReactNode;
  location: Location;
}

interface StateProps {
  loggedInUser: LoggedInUser | null;
  token: string;
}

interface Props extends OwnProps, StateProps {}

interface State {
  redirectUrl: string;
  redirectSearch?: string;
}

class AuthenticationStateChecker extends React.Component<Props, State> {
  public static getDerivedStateFromProps(nextProps: Props, state: State): State | null {
    const url = nextProps.location.pathname;
    const search = nextProps.location.search;
    const { token } = nextProps;
    const { redirectUrl } = state;

    if (!token && protectedRoutes.some((protectedRoute) => url.startsWith(protectedRoute))) {
      return { redirectUrl: url, redirectSearch: search };
    }

    if (token && redirectUrl !== PLACES && url !== ROOT_URL && url !== LOGIN_URL && url !== ACTIVATE_ACCOUNT_URL && url !== REGISTRATION_URL) {
      return { redirectUrl: PLACES };
    }

    return null;
  }

  public state: State = { redirectUrl: PLACES };

  public render() {
    const { token, location, children, loggedInUser } = this.props;
    const { redirectUrl, redirectSearch } = this.state;
    const url = location.pathname;

    if (token && (url === ROOT_URL || url === LOGIN_URL || url === ACTIVATE_ACCOUNT_URL || url === REGISTRATION_URL)) {
      return <Redirect to={{ pathname: redirectUrl, search: redirectSearch }} />;
    }

    if (!token && protectedRoutes.some((protectedRoute) => url.startsWith(protectedRoute))) {
      return <Redirect to={LOGIN_URL} />;
    }

    if (loggedInUser !== null && loggedInUser.deletionRequestedAt !== null && location.pathname !== DEACTIVATED_ACCOUNT) {
      return <Redirect to={DEACTIVATED_ACCOUNT} />;
    }

    if (loggedInUser !== null && loggedInUser.deletionRequestedAt === null && location.pathname === DEACTIVATED_ACCOUNT) {
      return <Redirect to={ROOT_URL} />;
    }

    const params: ConnectFetchParameter[] = [
      {
        dataFetcherAction: (fetchState) => fetchLoggedInUser({ fetchState }),
        fetchStateSelector: loggedInUserFetchStateSelector,
      },
    ];
    return <FetchContainer params={params}>{children}</FetchContainer>;
  }
}

const mapStateToProps = (state: Store) => ({ token: authenticationTokenSelector(state), loggedInUser: loggedInUserSelector(state) });

const actions = {};

const ConnectedAuthenticationStateChecker = connect<StateProps, object, OwnProps>(mapStateToProps, actions)(AuthenticationStateChecker);

export default function AuthenticationStateCheckerWithLocation(props: { children: React.ReactNode }) {
  const location = useLocation();

  return <ConnectedAuthenticationStateChecker {...props} location={location} />;
}
